import { useState, useRef, useEffect, useCallback } from 'react';
import { ToWords } from 'to-words';
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";
import '../styles/SingleCheck.css';
import { useCompany } from "../hooks/useCompany";
import { useUserProfile } from "../hooks/useUserProfile";
import RecommendedChecks from "../components/RecommendedChecks";

// Utility to format money values
const formatMoney = (number) => {
  const numberFloat = parseFloat(number || 0);
  return numberFloat.toLocaleString('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const toWordsTool = new ToWords({
  localeCode: 'en-US',
  converterOptions: {
    currency: true,
    ignoreDecimal: false,
    ignoreZeroCurrency: false,
    doNotAddOnly: true,
  },
});

const toWords = (denom) => {
  try {
    return toWordsTool.convert(Number(denom));
  } catch (e) {
    return `${e}`;
  }
};

const CheckBoxTemplate = ({ check, lineRef = null, extraClass = '' }) => (
  <div className={`check-box${extraClass ? ` ${extraClass}` : ''}`} id={extraClass ? undefined : 'check-box'}>
    <div style={{ position: 'relative' }} className="check-box-print">
      <div className="account-holder-name" style={{ position: 'absolute', top: '40px', left: '60px' }}>
        {check.accountHolderName}
      </div>
      <div className="account-holder-address" style={{ position: 'absolute', top: '70px', left: '60px' }}>
        {check.accountHolderAddress}
        <br />
        {check.accountHolderCity}, {check.accountHolderState} {check.accountHolderZip}
      </div>
      <div className="check-number-human" style={{ position: 'absolute', top: '40px', left: '1060px' }}>
        {check.checkNumber}
      </div>
      <div className="date-data" style={{ position: 'absolute', top: '80px', left: '850px' }}>
        {check.date}
      </div>
      <div className="date" style={{ position: 'absolute', top: '90px', left: '760px' }}>
        Date: _____________________
      </div>
      <div className="amount-box" style={{ position: 'absolute', top: '175px', left: '950px' }} />
      <div className="amount-data" style={{ position: 'absolute', top: '182px', left: '970px' }}>
        {formatMoney(check.amount)}
      </div>
      <div className="pay-to-data" style={{ position: 'absolute', top: '180px', left: '180px' }}>
        {check.payTo}
      </div>
      <div className="pay-to" style={{ position: 'absolute', top: '170px', left: '60px' }}>
        Pay to the <br />
        Order of <span className="payto-line"></span>
      </div>
      <div className="amount-line-data" ref={lineRef} style={{ position: 'absolute', top: '240px', left: '100px' }}>
        *** {toWords(check.amount)} ***
      </div>
      <div className="amount-line" style={{ position: 'absolute', top: '250px', left: '60px' }}>
        <span className="dollar-line"></span>
      </div>
      <div className="bank-name" style={{ position: 'absolute', top: '300px', left: '60px' }}>
        {check.bankName}
      </div>
      {/* The fractional routing number, under the bank's name (backend/bank_fraction.py). */}
      {check.bankFraction && (
        <div className="bank-fraction" style={{ position: 'absolute', top: '334px', left: '60px' }}>
          {check.bankFraction}
        </div>
      )}
      <div className="memo-data" style={{ position: 'absolute', top: '367px', left: '120px' }}>
        {check.memo}
      </div>
      <div className="memo" style={{ position: 'absolute', top: '390px', left: '60px' }}>
        Memo: ____________________________________
      </div>
      <div className="signature-data" style={{ position: 'absolute', top: '366px', left: '770px' }}>
        {check.signature}
      </div>
      <div className="signature" style={{ position: 'absolute', top: '390px', left: '750px' }}>
        ____________________________________________
      </div>
      <div className="banking" style={{ position: 'absolute', top: '420px', left: '80px' }}>
        <div className="routing" style={{ display: 'inline', marginLeft: '100px' }}>
          a{check.routingNumber}a
        </div>
        <div className="bank-account" style={{ display: 'inline', marginLeft: '100px' }}>
          {check.bankAccountNumber}c
        </div>
        <div className="check-number" style={{ display: 'inline', marginLeft: '100px' }}>
          {check.checkNumber}
        </div>
      </div>
    </div>
  </div>
);

// Generate a new check data object with blank defaults except date and checkNumber
const genNewCheck = () => ({
  accountHolderName: '',
  accountHolderAddress: '',
  accountHolderCity: '',
  accountHolderState: '',
  accountHolderZip: '',
  checkNumber: '0001', // default check number
  date: new Date().toLocaleDateString("en-US"), // default to today's date
  bankName: '',
  bankFraction: '',
  amount: '',
  payTo: '',
  memo: '',
  signature: '',
  routingNumber: '',
  bankAccountNumber: '',
});

const API_URL = process.env.REACT_APP_API_URL;

const SingleCheck = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { activeCompany, isDemo, refetch } = useCompany();
  const { profile } = useUserProfile();
  const [check, setCheck] = useState(genNewCheck());
  const lineRef = useRef(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [hideSummary, setHideSummary] = useState(false);

  // Compute whether any required account info is missing
  const requiredFields = [
    check.accountHolderName,
    check.accountHolderAddress,
    check.accountHolderCity,
    check.accountHolderState,
    check.accountHolderZip,
    check.bankName,
    check.routingNumber,
    check.bankAccountNumber,
  ];
  const missingInfo = requiredFields.some(field => !field);

  // Function to print the check with print-specific styles and the summary section
  const printCheck = useCallback(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        @page {
          margin: 0;
        }
        body {
          margin: 0;
          padding: 0;
        }
        /* Hide everything except the check-box and summary when printing */
        .wrapper > *:not(.check-box):not(.print-summary) {
          display: none !important;
        }
        /* Force the check to its intended dimensions */
        .check-box {
          width: 1200px !important;
          height: 750px !important;
          position: relative !important;
          overflow: visible !important;
          background: white !important;
          border: none !important;
        }
        .check-box-print {
          position: relative;
        }
        ${hideSummary
          ? `.print-summary { display: none !important; }`
          : `/* Show the summary and let it flow after the check */
          .print-summary {
            display: block !important;
            margin: 20px auto;
            text-align: center;
            font-size:20px;
            width: 100%;
            page-break-inside: avoid;
          }
          .print-summary h2 {
            margin-bottom: 20px;
          }
          .print-summary table {
            margin: 0 auto;
            text-align: left;
            border-collapse: collapse;
          }
          .print-summary td {
            padding: 4px 8px;
          }`
        }
      }
    `;
    document.head.appendChild(style);
    window.print();
    style.remove();
  }, [hideSummary]);

  // Function to fetch the latest check number from the backend
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!auth.isAuthenticated) return;
    try {
      const token = auth.user?.access_token;
      const response = await fetch(`${API_URL}/subscription/status`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);
      }
    } catch (err) {
      console.error("Error fetching subscription status:", err);
    }
  }, [auth]);

  // The next number after the company's latest check. For a bank account shared through the Olene
  // account, also past the highest number any Olene app used (backend checks/numbers.py).
  const fetchLatestCheckNumber = useCallback(async () => {
    if (!auth.isAuthenticated) return;
    try {
      const companyParam = activeCompany ? `?company_id=${activeCompany.company_id}` : '';
      const response = await fetch(`${API_URL}/checks/latest_number${companyParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${auth.user?.access_token}`,
        },
      });
      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(errorMessage);
      }
      const data = await response.json();
      if (data.check_number) setCheck(prev => ({ ...prev, checkNumber: data.check_number }));
    } catch (error) {
      console.error("Error fetching latest check number", error);
    }
  }, [auth.isAuthenticated, auth.user, activeCompany]);

  // Wrap the API POST in useCallback so it remains stable between renders.
  const postCheck = useCallback(async () => {
    const payload = {
      user_id: auth.user?.profile.sub,
      company_id: activeCompany?.company_id || null,
      accountHolderName: check.accountHolderName,
      accountHolderAddress: check.accountHolderAddress,
      accountHolderCity: check.accountHolderCity,
      accountHolderState: check.accountHolderState,
      accountHolderZip: check.accountHolderZip,
      checkNumber: check.checkNumber,
      date: check.date,
      bankName: check.bankName,
      bankFraction: check.bankFraction || null,
      amount: parseFloat(check.amount) || 0,
      payTo: check.payTo,
      memo: check.memo,
      signature: check.signature,
      routingNumber: check.routingNumber,
      bankAccountNumber: check.bankAccountNumber,
    };

    try {
      const response = await fetch(`${API_URL}/checks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.user?.access_token}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.status === 409 || response.status === 503) {
        // The number is shared with the company's Olene bank account: already used (pick another), or
        // couldn't be confirmed. Nothing was saved, so nothing prints.
        const detail = (await response.json().catch(() => ({})))?.detail || {};
        if (detail.code === 'check_number_used') {
          const { value } = await Swal.fire({
            icon: 'warning',
            title: t("check_number_used_title"),
            text: t("check_number_used_msg", { numbers: (detail.numbers || []).join(', ') }),
            input: 'text',
            inputValue: detail.next || '',
            showCancelButton: true,
            confirmButtonText: t("use_this_number"),
            cancelButtonText: t("cancel"),
            confirmButtonColor: '#3b82f6',
          });
          if (value && /^\d+$/.test(value.trim())) {
            setCheck(prev => ({ ...prev, checkNumber: value.trim() }));
          }
        } else if (detail.code === 'choose_bank') {
          Swal.fire({ icon: 'info', title: t("choose_bank_for_checks") });
        } else if (detail.code === 'bank_changed') {
          // Edited or removed in another Olene app: reload the company's numbers before printing.
          Swal.fire({ icon: 'info', title: t("bank_changed_msg") });
          refetch();
        } else {
          Swal.fire({ icon: 'error', title: t("check_numbers_unconfirmed") });
        }
        return null;
      }
      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(errorMessage);
      }
      const data = await response.json();
      console.log('Check posted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error posting check:', error);
      alert("Error posting check: " + error.message);
      return null;
    }
  }, [auth.user, check, activeCompany, t, refetch]);

  // Handler to print and post the check and then update the check number
  const handlePrintAndPost = useCallback(async () => {
    const result = await postCheck();
    if (result) {
      if (isDemo) {
        const newCount = parseInt(localStorage.getItem("demo_single_check_count") || "0", 10) + 1;
        localStorage.setItem("demo_single_check_count", String(newCount));
      }
      printCheck();
      fetchLatestCheckNumber();
    }
  }, [postCheck, printCheck, fetchLatestCheckNumber, isDemo]);

  // Handler for the Ctrl+P shortcut
  const handlePrintShortcut = useCallback(
    (event) => {
      if (event.ctrlKey && event.key === 'p') {
        event.preventDefault();
        handlePrintAndPost();
      }
    },
    [handlePrintAndPost]
  );

  // Update line width if needed (kept for styling)
  useEffect(() => {
    if (lineRef.current) {
      setCheck((prev) => ({ ...prev, lineLength: lineRef.current.clientWidth }));
    }
  }, []);

  // Register keydown event listener for printing
  useEffect(() => {
    window.addEventListener('keydown', handlePrintShortcut);
    return () => {
      window.removeEventListener('keydown', handlePrintShortcut);
    };
  }, [handlePrintShortcut]);

  // Populate fields from active company once available
  useEffect(() => {
    if (!auth.isAuthenticated || !activeCompany) return;
    setCheck((prev) => ({
      ...prev,
      accountHolderName:    activeCompany.name             || '',
      bankName:             activeCompany.bank_name        || '',
      bankFraction:         activeCompany.bank_fraction    || '',
      accountHolderAddress: activeCompany.address          || '',
      accountHolderCity:    activeCompany.city             || '',
      accountHolderState:   activeCompany.state            || '',
      accountHolderZip:     activeCompany.zip              || '',
      routingNumber:        activeCompany.routing_number   || '',
      bankAccountNumber:    activeCompany.account_number   || '',
      signature:            prev.signature || [
                              profile?.first_name,
                              profile?.middle_initial ? `${profile.middle_initial}.` : null,
                              profile?.last_name,
                            ].filter(Boolean).join(' ') || auth.user?.profile?.name || '',
    }));
  }, [auth.isAuthenticated, activeCompany, profile, auth.user?.profile?.name]);

  // Initially fetch the latest check number
  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchSubscriptionStatus();
      fetchLatestCheckNumber();
    }
  }, [auth.isAuthenticated, fetchSubscriptionStatus, fetchLatestCheckNumber]);

  const DEMO_CHECK_LIMIT = 3;
  const demoCheckCount = isDemo ? parseInt(localStorage.getItem("demo_single_check_count") || "0", 10) : 0;

  const hasActiveSubscription = () => {
    const status = subscriptionData?.subscriptionStatus?.toLowerCase();
    const isAdmin = subscriptionData?.is_admin === true;
    return isAdmin || status === "active" || status === "trialing" || status === "admin_bypass"
      || (isDemo && demoCheckCount < DEMO_CHECK_LIMIT);
  };

  const showSubscriptionPrompt = () => {
    Swal.fire({
      icon: 'info',
      title: t("subscription_required"),
      html: `
        <p>${t("sub_required_print_msg")}</p>
      `,
      showCancelButton: true,
      confirmButtonText: t("view_plans"),
      cancelButtonText: t("maybe_later"),
      confirmButtonColor: '#3b82f6'
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = '/ManageSubscription';
      }
    });
  };

  // Handler for form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCheck((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex-1">
        <div className="wrapper" id="wrapper" style={{ position: 'relative' }}>

      {/* The check graphic */}
      <CheckBoxTemplate check={check} lineRef={lineRef} />

      {/* Recommended check paper */}
      <RecommendedChecks className="no-print my-4 mx-auto w-full max-w-xl" />

      {/* The form for user input (hidden in print) */}
      <div className="check-data no-print" style={{ width: '100%' }}>
        <div className="max-w-xl mx-auto mt-4 sm:mt-10 p-4 sm:p-6 bg-white shadow-md rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <div className="alert alert-primary" role="alert">
              <strong>{t("update_company_info_msg")} {' '}
                <a href="/CompanySettings" target="_blank" rel="noopener noreferrer">
                  {t("company_settings_link")}
                </a>.
              </strong>
            </div>
          </div>
          <form>
            {/* Read-only fields pulled from Cognito */}
            <div className="mb-4">
              <label htmlFor="accountHolderName" className="block mb-2 text-gray-600">
                {t("account_holder_name")}
              </label>
              <input
                type="text"
                id="accountHolderName"
                name="accountHolderName"
                value={check.accountHolderName}
                onChange={handleInputChange}
                disabled
                className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="accountHolderAddress" className="block mb-2 text-gray-600">
                {t("col_address")}
              </label>
              <input
                type="text"
                id="accountHolderAddress"
                name="accountHolderAddress"
                value={check.accountHolderAddress}
                onChange={handleInputChange}
                disabled
                className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
              />
            </div>
            <div className="mb-4 flex space-x-4">
              <div className="flex-1">
                <label htmlFor="accountHolderCity" className="block mb-2 text-gray-600">
                  {t("city")}
                </label>
                <input
                  type="text"
                  id="accountHolderCity"
                  name="accountHolderCity"
                  value={check.accountHolderCity}
                  onChange={handleInputChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="accountHolderState" className="block mb-2 text-gray-600">
                  {t("state")}
                </label>
                <input
                  type="text"
                  id="accountHolderState"
                  name="accountHolderState"
                  value={check.accountHolderState}
                  onChange={handleInputChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="accountHolderZip" className="block mb-2 text-gray-600">
                  {t("zip")}
                </label>
                <input
                  type="text"
                  id="accountHolderZip"
                  name="accountHolderZip"
                  value={check.accountHolderZip}
                  onChange={handleInputChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>
            {/* From the company's manually added bank account, shared through the Olene account. */}
            <div className="mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t("manual_bank_title")}</p>
              <p className="text-xs text-gray-400">{t("manual_bank_hint")}</p>
            </div>
            <div className="mb-4 flex space-x-4">
              <div style={{ flexBasis: '33.33%' }}>
                <label htmlFor="checkNumber" className="block mb-2 text-gray-600">
                  {t("check_number")}
                </label>
                <input
                  type="text"
                  id="checkNumber"
                  name="checkNumber"
                  value={check.checkNumber}
                  onChange={handleInputChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div style={{ flexBasis: '66.67%' }}>
                <label htmlFor="bankName" className="block mb-2 text-gray-600">
                  {t("bank_name")}
                </label>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={check.bankName}
                  onChange={handleInputChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>
            <div className="mb-4 flex space-x-4">
              <div className="flex-1">
                <label htmlFor="routingNumber" className="block mb-2 text-gray-600">
                  {t("routing_num")}
                </label>
                <input
                  type="text"
                  id="routingNumber"
                  name="routingNumber"
                  value={check.routingNumber}
                  onChange={handleInputChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="bankAccountNumber" className="block mb-2 text-gray-600">
                  {t("account_num")}
                </label>
                <input
                  type="text"
                  id="bankAccountNumber"
                  name="bankAccountNumber"
                  value={check.bankAccountNumber}
                  onChange={handleInputChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>
            {/* Editable fields */}
            <div className="mb-4">
              <label htmlFor="memo" className="block mb-2 text-gray-600">
                {t("memo")}
              </label>
              <input
                type="text"
                id="memo"
                name="memo"
                value={check.memo}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-4 flex space-x-4">
              <div className="flex-1">
                <label htmlFor="amount" className="block mb-2 text-gray-600">
                  {t("amount")}
                </label>
                <input
                  type="text"
                  id="amount"
                  name="amount"
                  value={check.amount}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="payTo" className="block mb-2 text-gray-600">
                  {t("pay_to")}
                </label>
                <input
                  type="text"
                  id="payTo"
                  name="payTo"
                  value={check.payTo}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
            </div>
            <div className="mb-4 flex space-x-4">
              <div className="flex-1">
                <label htmlFor="date" className="block mb-2 text-gray-600">
                  {t("date")}
                </label>
                <input
                  type="text"
                  id="date"
                  name="date"
                  value={check.date}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="signature" className="block mb-2 text-gray-600">
                  {t("signature")}
                </label>
                <input
                  type="text"
                  id="signature"
                  name="signature"
                  value={check.signature}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
            </div>
            {/* Hide summary option */}
            <div className="mb-4 flex items-center">
              <input
                type="checkbox"
                id="hideSummary"
                checked={hideSummary}
                onChange={(e) => setHideSummary(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="hideSummary" className="text-gray-600 font-normal cursor-pointer">
                {t("hide_summary_label")}
              </label>
            </div>

            {/* Notification if missing required account info */}
            {missingInfo && (
              <div className="mb-4 text-red-600">
                {t("missing_bank_info")}{' '}
                <a href="/CompanySettings" target="_blank" rel="noopener noreferrer" className="underline">
                  {t("company_settings_link")}
                </a>{' '}
                {t("missing_bank_info_suffix")}
              </div>
            )}

            <div className="mt-4">
              <button
              type="button"
              className={`w-full py-2 rounded-lg transition ${
                missingInfo || !hasActiveSubscription()
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
              onClick={hasActiveSubscription() ? handlePrintAndPost : showSubscriptionPrompt}
              disabled={missingInfo || !hasActiveSubscription()}
              title={!hasActiveSubscription() ? (isDemo ? `Demo limit reached (${DEMO_CHECK_LIMIT} prints)` : 'Subscription required to print checks') : ''}
              >
                {hasActiveSubscription() ? (isDemo ? `${t("print_check")} (${demoCheckCount}/${DEMO_CHECK_LIMIT})` : t("print_check")) : (isDemo ? `Demo limit reached` : t("subscribe_to_print"))}
              </button>
              <Link to="/" className="block text-center text-blue-500 hover:underline mt-4">
                {t("back_to_dashboard")}
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Print-only summary section (hidden on screen) */}
      <div className="print-summary" style={{ display: 'none' }}>
        <table>
          <tbody>
            <tr>
              <td><strong>Account Holder Name:</strong></td>
              <td>{check.accountHolderName}</td>
            </tr>
            <tr>
              <td><strong>Address:</strong></td>
              <td>
                {check.accountHolderAddress}
                {check.accountHolderCity && `, ${check.accountHolderCity}`}
                {check.accountHolderState && `, ${check.accountHolderState}`}
                {check.accountHolderZip && ` ${check.accountHolderZip}`}
              </td>
            </tr>
            <tr>
              <td><strong>Check Number:</strong></td>
              <td>{check.checkNumber}</td>
            </tr>
            <tr>
              <td><strong>Date:</strong></td>
              <td>{check.date}</td>
            </tr>
            <tr>
              <td><strong>Bank Name:</strong></td>
              <td>{check.bankName}</td>
            </tr>
            <tr>
              <td><strong>Amount:</strong></td>
              <td>{formatMoney(check.amount)}</td>
            </tr>
            <tr>
              <td><strong>Pay To:</strong></td>
              <td>{check.payTo}</td>
            </tr>
            <tr>
              <td><strong>Memo:</strong></td>
              <td>{check.memo}</td>
            </tr>
            <tr>
              <td><strong>Signature:</strong></td>
              <td>{check.signature}</td>
            </tr>
            <tr>
              <td><strong>Routing Number:</strong></td>
              <td>{check.routingNumber}</td>
            </tr>
            <tr>
              <td><strong>Bank Account Number:</strong></td>
              <td>{check.bankAccountNumber}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
      </div>
      </Sidebar>
    </div>
  );
};

export default SingleCheck;
